import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp, getNodeTestProps } from '@/utils';
import { formatGel } from '../../sellerMeta';
import type { PaymentFlowValue } from '../usePaymentFlow';
import { PaymentEntryNotice } from './PaymentEntryNotice';

/**
 * Card step (render-only): no input needed — just a prompt to charge the card
 * terminal and the amount due. The footer's confirm books the sale directly.
 * Keys typed here are stray input (a barcode scanned, most likely):
 * `entryNoticeKey` then says so in a reserved slot under the amount. Reads `due`,
 * `isDueKnown` and `entryNoticeKey` from `usePaymentFlow`: an unknown due
 * (a discount that no longer fits) shows as `—`, never as a figure to charge.
 */
export const TerminalSection: React.FC<PaymentFlowValue> = ({
    due,
    isDueKnown,
    entryNoticeKey,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-col items-center gap-2 py-4 text-center"
            {...getNodeTestProps('seller-pay-terminal')}
        >
            <div className="flex size-16 items-center justify-center rounded-2xl bg-surface-secondary">
                <Icon
                    className="size-7 text-muted"
                    icon="solar:card-bold-duotone"
                />
            </div>
            <Typography color="muted" type="body-sm">
                {t('seller.payment.cardTerminal')}
            </Typography>
            <Typography
                className="tabular-nums leading-none"
                {...getNodeTestIdProp('seller-pay-terminal-amount')}
                type="h4"
                weight="bold"
            >
                {isDueKnown ? formatGel(due) : t('seller.discount.unknown')}
            </Typography>
            <PaymentEntryNotice messageKey={entryNoticeKey} method="card" />
        </div>
    );
};
